/* Phase 4.1 — the shared time-range model.
 *
 * Before this, "what time window am I looking at?" was answered three
 * different ways in three places: `lib/dateRange.ts`'s four presets, ten raw
 * <input type="date"> elements across six files, and per-page `useState`
 * pairs of from/to strings. Each rebuilt its own preset list, and none of
 * them could express the distinction that matters most in a SOC.
 *
 * ── Relative vs absolute is the whole point ──────────────────────────────
 * "Last 15 minutes" and "14:00–14:15 today" are not the same query even when
 * they currently resolve to the same window. The first MOVES — re-run it in
 * a minute and you get different rows, which is what you want while watching
 * an incident unfold. The second is PINNED — it is what you want while
 * writing up that incident, and re-resolving it under the analyst would
 * silently change the evidence.
 *
 * So a TimeRange keeps its own definition rather than being flattened to two
 * timestamps at the point of selection, and `resolve()` is called at query
 * time. A relative range therefore stays live across refreshes; an absolute
 * one cannot drift.
 */

export type RelativeUnit = "m" | "h" | "d";

export type TimeRange =
  | { kind: "all" }
  | { kind: "relative"; amount: number; unit: RelativeUnit }
  | { kind: "absolute"; from: string; to: string };

export interface ResolvedRange {
  /** RFC3339, or "" for an open lower bound. */
  from: string;
  /** RFC3339, or "" for an open upper bound. */
  to: string;
}

export const DEFAULT_RANGE: TimeRange = { kind: "relative", amount: 24, unit: "h" };

const UNIT_MS: Record<RelativeUnit, number> = {
  m: 60_000,
  h: 3_600_000,
  d: 86_400_000,
};

const UNIT_LABEL: Record<RelativeUnit, [one: string, many: string]> = {
  m: ["minute", "minutes"],
  h: ["hour", "hours"],
  d: ["day", "days"],
};

/* The presets a SOC actually reaches for. Deliberately weighted towards the
   short end: the long tail (90d, 1y) belongs to reporting, which uses an
   absolute range because a report has to be reproducible. */
export const RANGE_PRESETS: ReadonlyArray<{ label: string; short: string; range: TimeRange }> = [
  { label: "Last 15 minutes", short: "15m", range: { kind: "relative", amount: 15, unit: "m" } },
  { label: "Last 30 minutes", short: "30m", range: { kind: "relative", amount: 30, unit: "m" } },
  { label: "Last hour", short: "1h", range: { kind: "relative", amount: 1, unit: "h" } },
  { label: "Last 4 hours", short: "4h", range: { kind: "relative", amount: 4, unit: "h" } },
  { label: "Last 12 hours", short: "12h", range: { kind: "relative", amount: 12, unit: "h" } },
  { label: "Last 24 hours", short: "24h", range: { kind: "relative", amount: 24, unit: "h" } },
  { label: "Last 7 days", short: "7d", range: { kind: "relative", amount: 7, unit: "d" } },
  { label: "Last 30 days", short: "30d", range: { kind: "relative", amount: 30, unit: "d" } },
];

export function isRelative(r: TimeRange): r is Extract<TimeRange, { kind: "relative" }> {
  return r.kind === "relative";
}

/**
 * Turns a range into concrete bounds. Call this at QUERY time, not at
 * selection time — that is what keeps a relative range live.
 */
export function resolveRange(range: TimeRange, now: Date = new Date()): ResolvedRange {
  if (range.kind === "all") return { from: "", to: "" };
  if (range.kind === "absolute") return { from: range.from, to: range.to };
  /* Lower bound only, deliberately. An upper bound of "now" clips the newest
     rows whenever the client clock runs even slightly behind the ingest
     pipeline's — and in a SIEM the newest rows are the entire point. This
     matches the behaviour lib/dateRange.ts already had, and the reasoning is
     carried over rather than rediscovered. */
  return { from: new Date(now.getTime() - range.amount * UNIT_MS[range.unit]).toISOString(), to: "" };
}

/** Short form for a trigger button: "24h", "Jan 3 – Jan 5", "All time". */
export function formatRangeShort(range: TimeRange): string {
  if (range.kind === "all") return "All time";
  if (range.kind === "relative") return `${range.amount}${range.unit}`;
  const from = range.from ? shortDate(range.from) : "…";
  const to = range.to ? shortDate(range.to) : "now";
  return `${from} – ${to}`;
}

/** Full form for the accessible name and the panel header. */
export function formatRangeLong(range: TimeRange): string {
  if (range.kind === "all") return "All time";
  if (range.kind === "relative") {
    const [one, many] = UNIT_LABEL[range.unit];
    return `Last ${range.amount} ${range.amount === 1 ? one : many}`;
  }
  const from = range.from ? shortDate(range.from) : "the beginning";
  const to = range.to ? shortDate(range.to) : "now";
  return `${from} to ${to}`;
}

function shortDate(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/**
 * Parses the quick-entry syntax: `15m`, `2h`, `3d`, optionally prefixed with
 * "last". Returns null when the text is not a duration, so the caller can
 * leave the field alone rather than guessing.
 *
 * Borrowed from fal.ai's analytics range picker (Refero research), which
 * puts a free-text duration box above its calendar — for anyone who already
 * knows the window they want, typing "6h" beats hunting through a preset
 * list, and it covers the windows no preset list can enumerate.
 */
export function parseDuration(text: string): TimeRange | null {
  const m = /^\s*(?:last\s+)?(\d+)\s*(m|min|mins|minute|minutes|h|hr|hrs|hour|hours|d|day|days)\s*$/i.exec(text);
  if (!m) return null;
  const amount = Number(m[1]);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const u = m[2].toLowerCase();
  const unit: RelativeUnit = u.startsWith("d") ? "d" : u.startsWith("h") ? "h" : "m";
  return { kind: "relative", amount, unit };
}

/** For a native <input type="datetime-local">, which wants local wall time. */
export function toDateTimeLocal(v: string): string {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Inverse of toDateTimeLocal. "" stays "" so an open bound survives. */
export function fromDateTimeLocal(v: string): string {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}
